/**
 * Home's center pane for one For Review item: a Lightfield-style header
 * (client, prev/next, Dismiss, the primary action), the title and meta line,
 * a summary card and a "prepared work" card with the record's fields and
 * the per-kind actions (complete a reminder, open a thread, review an
 * intake, open the case/client, snooze).
 */
import dayjs from "dayjs";
import {
  ArrowUpRight,
  Check,
  ChevronDown,
  FileText,
  Sparkles,
  X
} from "lucide-react";
import { useState, type FC, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { ForeignNationalsAPI } from "@/api/foreignNationals";
import { agentLabel } from "@/features/agents/types";
import { isCalendarDate } from "@/lib/dateOnly";
import { cn } from "@/lib/utils";

import { shortDate, shortTime } from "../format";
import type {
  AttentionThreadRow,
  ExpirationRow,
  FilingDeadlineRow,
  IntakeRow,
  ReminderRow
} from "../types";
import { ClientAvatar } from "./ClientAvatar";
import { NavArrows, PaneBar } from "./PaneBar";
import {
  daysWaiting,
  dueLabel,
  KIND_ICON,
  KIND_LABEL,
  snoozeUntilTomorrow,
  type TriageItem
} from "./triageModel";

const AGENT_FOR_KIND: Record<TriageItem["kind"], string> = {
  overdue: "Casey",
  today: "Casey",
  approval: "Casey",
  mention: "Casey",
  intake: "Casey",
  filing: "Casey",
  expiring: "Casey",
  with_client: "Casey"
};

type Action = {
  /** Primary button text. */
  primary: string;
  /** The chip in the summary card (defaults to the primary). */
  chip?: string;
  /** Optional secondary button (always "Snooze" when present). */
  secondary?: "Snooze";
  /** Footer link label. */
  openLabel: string;
  /** The card's heading. */
  cardTitle: string;
};

/** The summary card for each agent outcome. */
const SIGNAL_ACTION: Record<
  NonNullable<TriageItem["signal"]>,
  Pick<Action, "chip" | "cardTitle">
> = {
  approval: { chip: "Needs Approval", cardTitle: "Approval Request" },
  question: { chip: "Answer", cardTitle: "Question" },
  failed: { chip: "Couldn't Finish", cardTitle: "Failed Run" },
  problems: { chip: "Review Problems", cardTitle: "Finished With Problems" },
  finished: { chip: "Finished", cardTitle: "Finished Run" }
};

const actionFor = (item: TriageItem): Action => {
  switch (item.kind) {
    case "overdue":
    case "today":
      return {
        primary: "Mark done",
        chip: "Mark done",
        secondary: "Snooze",
        openLabel: "Open in workspace",
        cardTitle:
          (item.row as ReminderRow).kind === "appointment"
            ? "Appointment"
            : "Reminder"
      };
    case "approval":
      return {
        primary: "Open thread",
        openLabel: "Open in workspace",
        ...SIGNAL_ACTION[item.signal ?? "approval"]
      };
    case "mention":
      return {
        primary: "Open channel",
        chip: "Reply",
        openLabel: "Open channel",
        cardTitle: "Mention"
      };
    case "intake":
      return {
        primary: "Review",
        chip: "Review",
        openLabel: "Open in workspace",
        cardTitle: "Intake submission"
      };
    case "with_client":
      return {
        primary: "Open intake",
        chip: "Waiting on client",
        secondary: "Snooze",
        openLabel: "Open in workspace",
        cardTitle: "Intake with the client"
      };
    case "filing":
      return {
        primary: "Open case",
        chip: "Open case",
        secondary: "Snooze",
        openLabel: "Open in workspace",
        cardTitle: "Filing deadline"
      };
    case "expiring":
    default:
      return {
        primary: "Open client",
        chip: "Open client",
        secondary: "Snooze",
        openLabel: "Open in workspace",
        cardTitle: "Document expiration"
      };
  }
};

const when = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const d = dayjs(iso);
  if (!d.isValid()) return "—";
  // A date with no time of day is a date, not "12:00 AM UTC".
  if (isCalendarDate(iso)) return shortDate(iso);
  return `${shortDate(iso)}, ${shortTime(iso)}`;
};

/** The key/value rows the card shows for each kind of record. */
export const fieldsFor = (item: TriageItem): Array<[string, string]> => {
  switch (item.kind) {
    case "overdue":
    case "today": {
      const r = item.row as ReminderRow;
      return [
        ["Due", r.all_day ? shortDate(r.due) : when(r.due)],
        ["Client", item.clientName],
        [
          "Assigned to",
          r.assignees?.length ? r.assignees.join(", ") : "Unassigned"
        ],
        ["Kind", r.kind === "appointment" ? "Appointment" : "Reminder"]
      ];
    }
    case "approval":
    case "mention": {
      const t = item.row as AttentionThreadRow;
      return [
        ["Channel", t.scope_kind === "client" ? item.clientName : "Practice"],
        ["Agent", agentLabel(t.agent_kind)],
        ["Why", item.caseLabel],
        ["Updated", when(t.updated_at)]
      ];
    }
    case "intake": {
      const i = item.row as IntakeRow;
      return [
        ["Client", item.clientName],
        ["Submitted by", i.recipient_email ?? "—"],
        ["Sent", when(i.sent_at)],
        ["Updated", when(i.updated_at)]
      ];
    }
    case "with_client": {
      const i = item.row as IntakeRow;
      return [
        ["Client", item.clientName],
        ["Status", item.caseLabel],
        ["Sent", when(i.sent_at)],
        ["Waiting", `${daysWaiting(i)} days`]
      ];
    }
    case "filing": {
      const d = item.row as FilingDeadlineRow;
      return [
        ["Applicant", d.applicant],
        ["Case", [d.visa_type, d.status].filter(Boolean).join(" · ") || "—"],
        ["Due", shortDate(d.due)],
        [
          "Assigned to",
          d.assignees?.length ? d.assignees.join(", ") : "Unassigned"
        ]
      ];
    }
    case "expiring":
    default: {
      const e = item.row as ExpirationRow;
      return [
        ["Document", e.label],
        ["Belongs to", e.subject],
        [item.overdue ? "Expired" : "Expires", shortDate(e.due)]
      ];
    }
  }
};

type Props = {
  item: TriageItem;
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  /** Hide the item (with Undo). `verb` leads the toast. */
  onRemove: (item: TriageItem, verb: string, until?: Date) => void;
  /** The summary payload should be refetched (a reminder was completed). */
  onChanged?: () => void;
  trailing?: ReactNode;
  now?: Date;
};

export const TriageDetail: FC<Props> = ({
  item,
  canPrev,
  canNext,
  onPrev,
  onNext,
  onRemove,
  onChanged,
  trailing,
  now = new Date()
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const Icon = KIND_ICON[item.kind];
  const action = actionFor(item);
  const fields = fieldsFor(item);
  const body = item.detail;
  const isReminder = item.kind === "overdue" || item.kind === "today";

  const complete = async () => {
    if (!item.clientId || !item.reminderId) return;
    setBusy(true);
    try {
      await ForeignNationalsAPI.updateReminder(item.clientId, item.reminderId, {
        status: "COMPLETED"
      });
      onRemove(item, "Done");
      onChanged?.();
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Couldn't complete the reminder"
      );
    } finally {
      setBusy(false);
    }
  };

  const primary = () => {
    if (isReminder) void complete();
    else navigate(item.path);
  };
  const snooze = () => onRemove(item, "Snoozed", snoozeUntilTomorrow(now));
  const dismiss = () => onRemove(item, "Dismissed");
  const openPath = () => navigate(item.path);

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="triage-detail">
      <PaneBar trailing={trailing}>
        <ClientAvatar name={item.clientName} size={20} />
        <span className="shrink-0 text-sm font-medium text-foreground">
          {item.clientName}
        </span>
        <span className="truncate text-sm text-muted-foreground">
          · {item.caseLabel}
        </span>
        <span className="flex-1" />
        <NavArrows
          onPrev={onPrev}
          onNext={onNext}
          canPrev={canPrev}
          canNext={canNext}
        />
        <span className="w-2 shrink-0" aria-hidden />
        <Button
          variant="ghost"
          size="sm"
          onClick={dismiss}
          data-testid="triage-dismiss"
        >
          <X className="size-3.5" />
          Dismiss
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={primary}
          disabled={busy}
          data-testid="triage-primary"
        >
          <Check className="size-3.5" />
          {action.primary}
        </Button>
      </PaneBar>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div
          key={item.id}
          className="mx-auto max-w-160 animate-in px-8 pt-6 pb-10 duration-150 fade-in slide-in-from-bottom-1"
        >
          <h1 className="text-xl leading-7 font-semibold tracking-[-0.01em] text-foreground">
            {item.title}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-base text-muted-foreground">
            {item.clientId ? (
              <button
                type="button"
                className="font-medium text-foreground hover:underline"
                onClick={() =>
                  navigate(
                    `/clients/${encodeURIComponent(item.clientId ?? "")}`
                  )
                }
              >
                {item.clientName}
              </button>
            ) : (
              <span className="font-medium text-foreground">
                {item.clientName}
              </span>
            )}
            <span>·</span>
            <span>{item.caseLabel}</span>
            <span>·</span>
            <span
              className={cn(item.overdue && "text-destructive")}
              data-testid="triage-due"
            >
              {dueLabel(item, now)}
            </span>
          </div>

          <div className="mt-5 rounded-xl bg-fill-1 px-3.5 py-3">
            <div className="flex items-center gap-2">
              <Icon className="size-4 text-muted-foreground" />
              <span className="text-base font-medium text-foreground">
                {KIND_LABEL[item.kind]}
              </span>
              <Button
                variant="outline"
                size="xs"
                className="h-5.5 text-xs font-normal"
                onClick={primary}
                disabled={busy}
              >
                {action.chip ?? action.primary}
              </Button>
              <span className="flex-1" />
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={open ? "Collapse" : "Expand"}
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                data-testid="triage-summary-toggle"
              >
                <ChevronDown
                  className={cn(
                    "size-3.5 transition-transform",
                    open && "rotate-180"
                  )}
                />
              </Button>
            </div>
            <p
              className={cn(
                "mt-2 text-sm leading-5.5 text-foreground",
                !open && "line-clamp-2"
              )}
              data-testid="triage-summary"
            >
              {body ||
                `${item.title} for ${item.clientName}${
                  item.due ? ` — ${dueLabel(item, now).toLowerCase()}` : ""
                }.`}
            </p>
            {open ? (
              <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                <Sparkles className="size-3" />
                Prepared by {AGENT_FOR_KIND[item.kind]} · from the practice
                summary
              </div>
            ) : null}
          </div>

          <div className="mt-3 overflow-hidden rounded-xl bg-elevated ring-1 ring-line-2">
            <div className="flex items-center gap-2 border-b border-line-1 px-3.5 py-2.5">
              <FileText className="size-3.5 text-muted-foreground" />
              <span className="flex-1 truncate text-base font-medium text-foreground">
                {action.cardTitle}
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Sparkles className="size-3" />
                {AGENT_FOR_KIND[item.kind]}
              </span>
            </div>
            {fields.map(([k, v]) => (
              <div
                key={k}
                className="grid gap-3 border-b border-border px-3.5 py-2.25 text-base leading-4.5"
                style={{ gridTemplateColumns: "112px minmax(0, 1fr)" }}
              >
                <span className="text-muted-foreground">{k}</span>
                <span className="text-foreground">{v}</span>
              </div>
            ))}
            {body && (item.kind === "approval" || item.kind === "mention") ? (
              <div className="px-4 py-3.5">
                <p className="text-sm leading-5.5 text-foreground">{body}</p>
              </div>
            ) : null}
            <div className="flex items-center gap-2 bg-fill-1 px-3 py-2.5">
              <Button
                variant="ghost"
                size="sm"
                onClick={openPath}
                data-testid="triage-open"
              >
                <ArrowUpRight className="size-3.5 text-muted-foreground" />
                {action.openLabel}
              </Button>
              <span className="flex-1" />
              {action.secondary ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={snooze}
                  data-testid="triage-snooze"
                >
                  {action.secondary}
                </Button>
              ) : null}
              <Button
                variant="primary"
                size="sm"
                onClick={primary}
                disabled={busy}
              >
                {action.primary}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
